import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircle2, FileText, FolderOpen, Loader2, Square, Upload, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';
import { isIngestInFlight, type IngestJobSnapshot, useProjectIngestJob } from '../../hooks/useProjectIngestJob';

const ACCEPT_EXT = new Set(['.pdf', '.txt', '.doc', '.docx', '.csv', '.xlsx', '.xls', '.json', '.md']);
const SKIP_NAMES = new Set(['.ds_store', 'thumbs.db', 'desktop.ini']);

export type UrsInterview = {
  source_id: string;
  filename: string;
  status: string;
  state?: 'stuck' | 'failed' | 'in_progress' | 'ready';
  age_minutes?: number;
  failure_reason?: string | null;
};

export type UrsCorpus = {
  usable: UrsInterview[];
  inProgress: UrsInterview[];
  needsAttention: UrsInterview[];
};

type ProjectDoc = {
  id: number;
  original_name: string;
  analysis_status: string;
  pii_status?: string;
};

function isAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  if (SKIP_NAMES.has(name) || name.startsWith('.')) return false;
  if (file.webkitRelativePath && /__macosx/i.test(file.webkitRelativePath)) return false;
  const dot = name.lastIndexOf('.');
  if (dot < 0) return false;
  return ACCEPT_EXT.has(name.slice(dot));
}

function mergeFiles(prev: File[], incoming: File[]): File[] {
  const seen = new Set(prev.map((f) => `${f.name}:${f.size}:${f.lastModified}`));
  const next = [...prev];
  for (const f of incoming) {
    if (!isAcceptedFile(f)) continue;
    const key = `${f.name}:${f.size}:${f.lastModified}`;
    if (seen.has(key)) continue;
    seen.add(key);
    next.push(f);
  }
  return next;
}

function readEntryAsFile(entry: any): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

function readDir(entry: any): Promise<any[]> {
  return new Promise((resolve, reject) => {
    const reader = entry.createReader();
    const all: any[] = [];
    const read = () => {
      reader.readEntries((batch) => {
        if (!batch.length) return resolve(all);
        all.push(...batch);
        read();
      }, reject);
    };
    read();
  });
}

async function walkEntry(entry: any, out: File[]) {
  if (entry.isFile) {
    const file = await readEntryAsFile(entry);
    out.push(file);
    return;
  }
  if (entry.isDirectory) {
    if (/^__macosx$/i.test(entry.name)) return;
    const children = await readDir(entry);
    for (const child of children) await walkEntry(child, out);
  }
}

async function filesFromDrop(e: React.DragEvent): Promise<File[]> {
  const items = e.dataTransfer?.items;
  if (items && items.length && typeof (items[0] as any).webkitGetAsEntry === 'function') {
    const out: File[] = [];
    for (let i = 0; i < items.length; i += 1) {
      const entry = (items[i] as any).webkitGetAsEntry?.();
      if (entry) await walkEntry(entry, out);
    }
    return out.filter(isAcceptedFile);
  }
  return Array.from(e.dataTransfer?.files || []).filter(isAcceptedFile);
}

interface UrsWorkspaceProps {
  projectId: number | undefined;
  ingest: ReturnType<typeof useProjectIngestJob>;
  corpus: UrsCorpus;
  selectedSourceIds: string[];
  onToggleSource: (sourceId: string) => void;
  onSelectAllSources: (ids: string[]) => void;
  onRemoveIssue: (issue: UrsInterview) => void;
  removingId: string | null;
  onIngestSettled: (snap: IngestJobSnapshot) => void;
}

export default function UrsWorkspace({
  projectId,
  ingest,
  corpus,
  selectedSourceIds,
  onToggleSource,
  onSelectAllSources,
  onRemoveIssue,
  removingId,
  onIngestSettled,
}: UrsWorkspaceProps) {
  const [staged, setStaged] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [docs, setDocs] = useState<ProjectDoc[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [pickedDocIds, setPickedDocIds] = useState<number[]>([]);
  const [skippedDocIds, setSkippedDocIds] = useState<number[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const lastTerminalJob = useRef<string | null>(null);

  const { snapshot, ingesting, startFiles, startFromDocuments, cancel, skipFile } = ingest;

  useEffect(() => {
    const el = folderInputRef.current;
    if (!el) return;
    el.setAttribute('webkitdirectory', '');
    el.setAttribute('directory', '');
  }, []);

  useEffect(() => {
    if (!snapshot.jobId) return;
    if (isIngestInFlight(snapshot.state) || snapshot.state === 'uploading') return;
    if (lastTerminalJob.current === snapshot.jobId) return;
    lastTerminalJob.current = snapshot.jobId;
    onIngestSettled(snapshot);
  }, [snapshot, onIngestSettled]);

  const usableNames = useMemo(
    () => new Set(corpus.usable.map((u) => u.filename.toLowerCase())),
    [corpus.usable],
  );

  const fetchDocs = useCallback(async () => {
    if (!projectId) {
      setDocs([]);
      return;
    }
    setDocsLoading(true);
    try {
      const res = await api.get(`/api/documents/project/${projectId}`, {
        params: { limit: 500 },
      });
      const list: ProjectDoc[] = res.data?.documents || res.data?.data || res.data || [];
      setDocs(Array.isArray(list) ? list : []);
    } catch {
      setDocs([]);
    } finally {
      setDocsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void fetchDocs();
    setSkippedDocIds([]);
    setPickedDocIds([]);
  }, [fetchDocs]);

  const addIncoming = useCallback((files: File[]) => {
    setStaged((prev) => mergeFiles(prev, files));
  }, []);

  const beginIngest = async (files: File[]) => {
    const accepted = files.filter(isAcceptedFile);
    if (!accepted.length) {
      if (files.length) {
        toast.error('That folder had no supported transcripts (PDF, Word, text, Excel, JSON, Markdown).');
      }
      return;
    }
    if (!projectId) {
      addIncoming(accepted);
      return;
    }
    await startFiles(projectId, accepted);
  };

  const startStaged = async () => {
    if (!projectId || staged.length === 0) return;
    const files = staged;
    setStaged([]);
    await startFiles(projectId, files);
  };

  const loadDocs = async () => {
    setDocsOpen(true);
    await fetchDocs();
  };

  const ingestPickedDocs = async () => {
    if (!projectId || pickedDocIds.length === 0) return;
    const ids = pickedDocIds.filter((id) => !skippedDocIds.includes(id));
    setPickedDocIds([]);
    setDocsOpen(false);
    if (!ids.length) return;
    await startFromDocuments(projectId, ids);
  };

  const skipProcessDoc = (doc: ProjectDoc) => {
    setSkippedDocIds((prev) => (prev.includes(doc.id) ? prev : [...prev, doc.id]));
    setPickedDocIds((prev) => prev.filter((id) => id !== doc.id));
    if (ingesting && projectId && doc.original_name) {
      void skipFile(projectId, doc.original_name);
    }
  };

  const progressLabel = (() => {
    const d = snapshot.progressDetail;
    const active = Array.isArray(d?.active) ? d.active.filter(Boolean) : [];
    if (d?.total && active.length) {
      return `${d.current || 0} of ${d.total} interviews · reading ${active.join(', ')}`;
    }
    if (d?.total && d.filename) {
      return `${d.current || 0} of ${d.total} interviews · reading ${d.filename}`;
    }
    if (ingesting) return snapshot.state === 'uploading' ? 'Uploading transcripts…' : 'Reading transcripts in full. This takes a few minutes per interview.';
    return null;
  })();

  const skippedCount = snapshot.ingestSkipped.length;
  const readyCount = corpus.usable.length;
  const currentName = (snapshot.progressDetail?.filename || '').toLowerCase();
  const errorNames = snapshot.ingestErrors.map((e) => e.split(':')[0].trim().toLowerCase());
  const skippedNames = [
    ...snapshot.ingestSkipped.map((e) => e.split(':')[0].trim().toLowerCase()),
    ...(snapshot.skipFiles || []).map((n) => n.toLowerCase()),
  ];
  const usableNamesLower = corpus.usable.map((u) => u.filename.toLowerCase());
  const inFlightNames = corpus.inProgress.map((u) => u.filename.toLowerCase());
  const failedNames = corpus.needsAttention.map((u) => u.filename.toLowerCase());
  const activeNames = (snapshot.progressDetail?.active || []).map((n) => n.toLowerCase());
  const fileRows = snapshot.files.map((name) => {
    const lower = name.toLowerCase();
    let status: 'queued' | 'reading' | 'ready' | 'skipped' | 'failed' = 'queued';
    if (errorNames.includes(lower) || failedNames.includes(lower)) status = 'failed';
    else if (skippedNames.includes(lower)) status = 'skipped';
    else if (usableNamesLower.includes(lower)) status = 'ready';
    else if (inFlightNames.includes(lower) || activeNames.includes(lower) || lower === currentName) status = 'reading';
    else if (!ingesting && usableNamesLower.includes(lower)) status = 'ready';
    else if (!ingesting) status = 'queued';
    return { name, status };
  });
  const showRestart = !ingesting && (snapshot.state === 'cancelled' || snapshot.state === 'failed');
  const visibleDocs = docs.filter((d) => (
    d.pii_status !== 'blocked'
    && d.analysis_status !== 'failed'
    && !skippedDocIds.includes(d.id)
  ));

  return (
    <div className="mb-5 space-y-4">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.txt,.doc,.docx,.csv,.xlsx,.xls,.json,.md"
        className="hidden"
        onChange={(e) => {
          addIncoming(Array.from(e.target.files || []));
          e.target.value = '';
        }}
      />
      <input
        ref={folderInputRef}
        type="file"
        multiple
        // @ts-expect-error webkitdirectory is a non-standard attribute the folder picker needs
        webkitdirectory=""
        className="hidden"
        onChange={(e) => {
          const incoming = Array.from(e.target.files || []);
          e.target.value = '';
          void beginIngest(incoming);
        }}
      />

      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={async (e) => {
          e.preventDefault();
          setDragOver(false);
          const incoming = await filesFromDrop(e);
          const fromFolder = incoming.some((f) => Boolean(f.webkitRelativePath));
          if (fromFolder) void beginIngest(incoming);
          else addIncoming(incoming);
        }}
        className={`border-2 border-dashed rounded-xl px-5 py-4 cursor-pointer transition-all ${
          dragOver ? 'border-purple-400 bg-purple-50' : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
        }`}
      >
        <div className="flex flex-col items-center justify-center gap-2">
          <Upload className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <p className="text-sm text-gray-500">Drop files or a folder, or click to upload</p>
          <p className="text-xs text-gray-400">
            {staged.length > 0
              ? `Add more files (${staged.length} staged)`
              : readyCount > 0
                ? `${readyCount} interview${readyCount === 1 ? '' : 's'} already in this project — add more anytime`
                : 'PDF, Word, text, Excel, JSON'}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); folderInputRef.current?.click(); }}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-purple-700 hover:text-purple-900"
        >
          <FolderOpen className="w-3.5 h-3.5" /> Upload folder
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); void loadDocs(); }}
          disabled={!projectId}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-purple-700 hover:text-purple-900 disabled:text-gray-300"
        >
          <FileText className="w-3.5 h-3.5" /> Choose from Process Documents
        </button>
      </div>

      {staged.length > 0 && (
        <div>
          <div className="flex flex-wrap gap-1.5">
            {staged.map((f, i) => (
              <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 border border-purple-200 rounded-full text-xs text-purple-700 font-medium">
                <FileText className="w-3 h-3" />{f.name}
                <button
                  type="button"
                  onClick={() => setStaged((prev) => prev.filter((_, idx) => idx !== i))}
                  className="ml-0.5 text-purple-400 hover:text-red-500"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              onClick={() => void startStaged()}
              disabled={!projectId || ingesting}
              className="text-xs font-semibold text-white bg-purple-700 hover:bg-purple-800 disabled:bg-gray-200 disabled:text-gray-400 rounded-lg px-3 py-1.5"
            >
              Add {staged.length} transcript{staged.length === 1 ? '' : 's'}
            </button>
            <button type="button" onClick={() => setStaged([])} className="text-xs text-red-400 hover:text-red-600">Clear staged</button>
          </div>
        </div>
      )}

      {(visibleDocs.length > 0 || docsOpen) && (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-gray-700">Process Documents in this project</p>
            {docsOpen && (
              <button type="button" onClick={() => setDocsOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            )}
          </div>
          {docsLoading && docs.length === 0 ? (
            <p className="text-xs text-gray-400">Loading documents…</p>
          ) : visibleDocs.length === 0 ? (
            <p className="text-xs text-gray-500">No documents on the Process Documentation tab yet.</p>
          ) : (
            <div className="max-h-48 overflow-y-auto space-y-1">
              {visibleDocs.map((d) => {
                const already = usableNames.has((d.original_name || '').toLowerCase());
                const checked = pickedDocIds.includes(d.id);
                const status = (d.analysis_status || 'pending').replace(/_/g, ' ');
                return (
                  <label key={d.id} className="flex items-center gap-2 text-xs text-gray-700 py-1">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={already}
                      onChange={() => setPickedDocIds((prev) => (
                        prev.includes(d.id) ? prev.filter((id) => id !== d.id) : [...prev, d.id]
                      ))}
                    />
                    <span className="truncate flex-1">{d.original_name}</span>
                    <span className="shrink-0 text-[10px] uppercase tracking-wide text-gray-400">{already ? 'already ingested' : status}</span>
                    {!already && (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); skipProcessDoc(d); }}
                        className="shrink-0 text-[11px] font-medium text-gray-500 hover:text-red-600"
                      >
                        Skip
                      </button>
                    )}
                  </label>
                );
              })}
            </div>
          )}
          {pickedDocIds.length > 0 && (
            <button
              type="button"
              onClick={() => void ingestPickedDocs()}
              disabled={ingesting}
              className="mt-2 text-xs font-semibold text-white bg-purple-700 hover:bg-purple-800 rounded-lg px-3 py-1.5 disabled:bg-gray-200"
            >
              Ingest {pickedDocIds.length} document{pickedDocIds.length === 1 ? '' : 's'}
            </button>
          )}
        </div>
      )}

      {(ingesting || progressLabel || fileRows.length > 0) && (
        <div className="rounded-xl border border-purple-200 bg-purple-50 px-4 py-3">
          {(ingesting || progressLabel) && (
            <>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs text-purple-800 min-w-0">
                  <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                  <span className="truncate">{progressLabel}</span>
                </div>
                {projectId && ingesting && (
                  <button
                    type="button"
                    onClick={() => void cancel(projectId)}
                    className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-red-700 hover:text-red-900"
                  >
                    <Square className="w-3 h-3" /> Stop
                  </button>
                )}
              </div>
              <div className="mt-2 h-1.5 bg-purple-200 rounded-full overflow-hidden">
                <div className="h-full bg-purple-600 transition-all" style={{ width: `${Math.min(100, snapshot.progress || 0)}%` }} />
              </div>
              <p className="mt-2 text-[11px] text-purple-700">
                {readyCount} interview{readyCount === 1 ? '' : 's'} stored.
                {skippedCount > 0 ? ` ${skippedCount} already analyzed (skipped).` : ''}
                {' '}New files slot into the existing taxonomy — finished interviews are not re-read.
              </p>
            </>
          )}
          {fileRows.length > 0 && (
            <ul className={`${ingesting || progressLabel ? 'mt-2' : ''} space-y-0.5 max-h-32 overflow-y-auto`}>
              {fileRows.map((row) => (
                <li key={row.name} className="flex items-center gap-2 text-[11px] text-purple-900">
                  <span className="w-14 shrink-0 text-purple-600">{row.status}</span>
                  <span className="truncate flex-1">{row.name}</span>
                  {ingesting && projectId && (row.status === 'queued' || row.status === 'reading') && (
                    <button
                      type="button"
                      onClick={() => void skipFile(projectId, row.name)}
                      className="shrink-0 text-[11px] font-medium text-gray-500 hover:text-red-600"
                    >
                      Skip
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showRestart && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex flex-wrap items-center gap-2">
          <p className="text-xs text-amber-900 flex-1">
            Ingest stopped. Finished interviews stay in the taxonomy. Re-drop the folder to restart remaining files — already analyzed ones skip.
          </p>
          <button
            type="button"
            onClick={() => folderInputRef.current?.click()}
            className="text-xs font-semibold text-white bg-purple-700 hover:bg-purple-800 rounded-lg px-3 py-1.5"
          >
            Restart remaining
          </button>
        </div>
      )}

      {snapshot.ingestSkipped.length > 0 && !ingesting && (
        <p className="text-xs text-gray-500">{snapshot.ingestSkipped.length} file{snapshot.ingestSkipped.length === 1 ? '' : 's'} already in this project (skipped, not re-extracted).</p>
      )}
      {snapshot.ingestErrors.length > 0 && (
        <div className="text-xs text-red-700 space-y-0.5">
          {snapshot.ingestErrors.map((err) => <div key={err}>{err}</div>)}
        </div>
      )}
      {snapshot.error && snapshot.state !== 'active' && (
        <p className="text-xs text-amber-800">{snapshot.error}</p>
      )}

      {corpus.usable.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-gray-700">
              Interviews in this project ({selectedSourceIds.length} of {corpus.usable.length} included)
            </p>
            <button
              type="button"
              className="text-[11px] text-purple-700 hover:text-purple-900"
              onClick={() => onSelectAllSources(
                selectedSourceIds.length === corpus.usable.length ? [] : corpus.usable.map((u) => u.source_id),
              )}
            >
              {selectedSourceIds.length === corpus.usable.length ? 'Include none' : 'Include all'}
            </button>
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {corpus.usable.map((u) => (
              <label key={u.source_id} className="flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={selectedSourceIds.includes(u.source_id)}
                  onChange={() => onToggleSource(u.source_id)}
                />
                <CheckCircle2 className="w-3 h-3 text-green-600 shrink-0" />
                <span className="truncate">{u.filename}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {corpus.inProgress.length > 0 && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-900">
          {corpus.inProgress.map((row) => (
            <div key={row.source_id} className="flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              Reading {row.filename}
            </div>
          ))}
        </div>
      )}

      {corpus.needsAttention.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
          <div className="text-sm font-semibold text-amber-900 mb-1">
            {corpus.needsAttention.length} transcript{corpus.needsAttention.length === 1 ? '' : 's'} did not finish
          </div>
          <div className="text-xs text-amber-800 mb-2">
            Nothing in the report comes from {corpus.needsAttention.length === 1 ? 'it' : 'them'}.
            Retry to upload the same file again — finished interviews stay in the taxonomy.
          </div>
          <div className="space-y-1.5">
            {corpus.needsAttention.map((issue) => (
              <div key={issue.source_id} className="flex items-center justify-between gap-3 bg-white border border-amber-200 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <div className="text-xs font-medium text-gray-800 truncate">{issue.filename}</div>
                  <div className="text-[11px] text-gray-500">
                    {issue.state === 'failed' ? 'Failed' : 'Stuck'} at &quot;{issue.status}&quot;
                    {issue.failure_reason ? ` · ${issue.failure_reason}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemoveIssue(issue)}
                  disabled={removingId === issue.source_id}
                  className="shrink-0 text-xs font-medium text-red-600 hover:text-red-800 disabled:text-gray-400"
                >
                  {removingId === issue.source_id ? 'Retrying…' : 'Retry'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
